// ─────────────────────────────────────────────────────────────
// LA MER
//
//  · la houle suit les DIAGONALES de la grille isométrique
//  · l'eau s'éclaircit en approchant des terres (haut-fond)
//  · l'écume épouse le contour réel du losange, pas une ellipse
//  · chaque île projette un reflet sombre dessous
//  · trois vitesses de dérive superposées, plus des caustiques
//
// CHANGEMENT : ces fonctions recevaient un identifiant d'ambiance
// (« jour », « crepuscule », « nuit ») et choisissaient une palette
// dans un tableau. C'était un PALIER : au basculement, tout l'océan
// changeait de couleur d'un coup — la cassure jour/nuit la plus
// visible du jeu. Elles reçoivent maintenant directement la palette,
// déjà interpolée par skyAt() (voir sky.js), donc la mer se teinte
// en continu comme le reste de la scène.
// ─────────────────────────────────────────────────────────────
import { ISO_W, ISO_H } from "./tileset.js";

// Angle des diagonales iso (≈ 25°) : c'est l'axe de la houle.
const WAVE_ANGLE = Math.atan2(ISO_H, ISO_W);

// Conservé pour les rares appels qui passeraient encore un identifiant.
export const SEA = {
  jour: { deep: "#14608a", mid: "#2f8fb8", shallow: "#7fd4e8", foam: "#ffffff", glint: "#e8fbff" },
  crepuscule: { deep: "#123a58", mid: "#2a6d92", shallow: "#6fb9cf", foam: "#ffe6c8", glint: "#ffd9a8" },
  nuit: { deep: "#08182b", mid: "#14304f", shallow: "#2f5f80", foam: "#c8d8f0", glint: "#9fc4e8" },
};

// `mood` peut être une palette complète (cas normal) ou, par
// rétro-compatibilité, un identifiant d'ambiance.
const paletteOf = (mood) =>
  (mood && typeof mood === "object" ? mood : SEA[mood]) || SEA.crepuscule;

// ── Fond de mer (espace écran) ────────────────────────────────
export function drawSeaBase(ctx, W, H, t, mood) {
  const C = paletteOf(mood);

  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, C.mid);
  g.addColorStop(0.45, C.deep);
  g.addColorStop(1, C.deep);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);

  const layers = [
    { gap: 96, len: 150, w: 5, a: 0.10, sp: 14, seed: 0 },
    { gap: 64, len: 96, w: 3.5, a: 0.085, sp: -22, seed: 37 },
    { gap: 132, len: 210, w: 7, a: 0.06, sp: 9, seed: 71 },
  ];
  const diag = Math.hypot(W, H) * 1.4;

  ctx.save();
  ctx.translate(W / 2, H / 2);
  ctx.rotate(WAVE_ANGLE);
  ctx.lineCap = "round";
  for (const L of layers) {
    ctx.strokeStyle = C.glint;
    ctx.globalAlpha = L.a;
    ctx.lineWidth = L.w;
    const drift = (t * L.sp) % L.gap;
    for (let row = -diag / 2; row < diag / 2; row += L.gap) {
      const y = row + drift;
      for (let x = -diag / 2; x < diag / 2; x += L.len * 2) {
        const jitter = Math.sin((x + row) * 0.006 + t * 0.5 + L.seed) * 14;
        const alpha = 0.55 + 0.45 * Math.sin((x - row) * 0.004 + t * 0.9 + L.seed);
        ctx.globalAlpha = L.a * alpha;
        ctx.beginPath();
        ctx.moveTo(x, y + jitter);
        ctx.lineTo(x + L.len, y + jitter + Math.sin(t + x * 0.01) * 4);
        ctx.stroke();
      }
    }
  }
  ctx.restore();

  // Caustiques : petits éclats qui apparaissent et disparaissent
  ctx.save();
  ctx.fillStyle = C.glint;
  for (let i = 0; i < 70; i++) {
    const bx = (i * 149.3) % W;
    const by = (i * 97.7 + Math.sin(t * 0.3 + i) * 20) % H;
    const tw = Math.sin(t * 1.6 + i * 2.3);
    if (tw <= 0.78) continue;
    ctx.globalAlpha = (tw - 0.78) * 3.2;
    const l = 5 + tw * 12;
    ctx.save();
    ctx.translate(bx, by);
    ctx.rotate(WAVE_ANGLE);
    ctx.fillRect(-l / 2, -1, l, 2);
    ctx.restore();
  }
  ctx.restore();
}

// ── Hauts-fonds et écume ──────────────────────────────────────
// On part des LOSANGES DE CHAQUE CASE de terre, on en fait l'union,
// et on dilate cette union : le contour épouse la forme réelle,
// découpes de coins comprises.
function paintUnion(oc, polys, grow, color) {
  oc.fillStyle = color;
  oc.strokeStyle = color;
  oc.lineJoin = "round";
  oc.lineCap = "round";
  oc.lineWidth = Math.max(0.01, grow * 2);
  for (const poly of polys) {
    oc.beginPath();
    poly.forEach((p, i) => (i ? oc.lineTo(p.x, p.y) : oc.moveTo(p.x, p.y)));
    oc.closePath();
    if (grow > 0) oc.stroke();
    oc.fill();
  }
}

// Compose tout le décor de mer (hauts-fonds + écume + reflet) sur un
// canvas hors écran. Coûteux : recalculé seulement quand la caméra,
// l'archipel ou l'ambiance changent — pas à chaque frame.
export function buildSeaDecor(off, tilePolys, W, H, S, mood) {
  const C = paletteOf(mood);
  if (off.width !== W) off.width = W;
  if (off.height !== H) off.height = H;
  const oc = off.getContext("2d");
  oc.setTransform(1, 0, 0, 1, 0, 0);
  oc.clearRect(0, 0, W, H);

  const tmp = document.createElement("canvas");
  tmp.width = W; tmp.height = H;
  const tc = tmp.getContext("2d");

  const compose = (grow, color, alpha, blur, erode) => {
    tc.setTransform(1, 0, 0, 1, 0, 0);
    tc.clearRect(0, 0, W, H);
    tc.globalCompositeOperation = "source-over";
    paintUnion(tc, tilePolys, grow * S, color);
    if (erode != null) {
      tc.globalCompositeOperation = "destination-out";
      paintUnion(tc, tilePolys, erode * S, "#000");
      tc.globalCompositeOperation = "source-over";
    }
    oc.save();
    oc.globalAlpha = alpha;
    if ("filter" in oc) oc.filter = `blur(${blur * S}px)`;
    oc.drawImage(tmp, 0, 0);
    oc.restore();
  };

  // reflet sombre, décalé vers le bas
  tc.setTransform(1, 0, 0, 1, 0, 0);
  tc.clearRect(0, 0, W, H);
  paintUnion(tc, tilePolys.map((poly) => poly.map((p) => ({ x: p.x, y: p.y + 26 * S }))), 0, "#03121f");
  oc.save();
  oc.globalAlpha = 0.22;
  if ("filter" in oc) oc.filter = `blur(${5 * S}px)`;
  oc.drawImage(tmp, 0, 0);
  oc.restore();

  // paliers de profondeur, du large vers la plage
  compose(70, C.mid, 0.26, 30);
  compose(44, C.mid, 0.26, 20);
  compose(26, C.shallow, 0.24, 13);
  compose(13, C.shallow, 0.34, 7);

  // écume : bande entre deux dilatations
  compose(11, C.foam, 0.42, 2, 3);
  compose(20, C.foam, 0.16, 7, 11);

  return off;
}
